'use client'

import * as React from 'react'
import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import type { RoyaltyDetailBookEntry } from '@package/api'
import type { ServerTableData } from '@helper/utils/types'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/web/components'
import cn from '@config/tailwind/cn'

import { royaltyDetailsColumns } from './columns'

interface RoyaltyDetailsTableProps {
  data: RoyaltyDetailBookEntry[]
  isLoading?: boolean
  className?: string
  downloadButton?: React.ReactNode
  pagination?: ServerTableData<RoyaltyDetailBookEntry>['pagination']
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
}

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

export function RoyaltyDetailsTable({
  data,
  isLoading,
  className,
  downloadButton: _downloadButton,
  pagination,
  onPageChange,
  onPageSizeChange,
}: RoyaltyDetailsTableProps) {
  const table = useReactTable({
    data,
    columns: royaltyDetailsColumns,
    getCoreRowModel: getCoreRowModel(),
    ...(pagination
      ? { manualPagination: true, pageCount: pagination.pageCount }
      : { getPaginationRowModel: getPaginationRowModel(), initialState: { pagination: { pageSize: 10 } } }),
    getRowId: (row) => row.id,
  })

  const totalCount = pagination?.total ?? data.length
  const pageCount = pagination?.pageCount ?? table.getPageCount()
  const currentPage = pagination?.page ?? table.getState().pagination.pageIndex + 1
  const serverMode = Boolean(pagination && onPageChange)

  if (isLoading) {
    return (
      <div className={cn('srd:border-neutrals-n-20 srd:overflow-hidden srd:rounded-md srd:border', className)}>
        <TableComponent>
          <TableHeader className='srd:bg-neutrals-nvar-10'>
            <TableRow>
              {royaltyDetailsColumns.map((col, index) => (
                <TableHead key={col.id ?? `col-${index}`} className='srd:text-nowrap'>
                  {typeof col.header === 'function' ? col.header({} as never) : col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                {royaltyDetailsColumns.map((_, j) => (
                  <TableCell key={j}>
                    <div className='srd:bg-neutrals-n-20 srd:h-6 srd:animate-pulse srd:rounded' />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </TableComponent>
      </div>
    )
  }

  return (
    <section className={cn('srd:w-full srd:space-y-4', className)}>
      <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-4'>
        {/* {downloadButton} */}
        <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-end srd:gap-2'>
          <span className='srd:text-neutrals-n-75 srd:text-sm'>
            Total: <span className='srd:text-foreground srd:font-medium'>{totalCount}</span>
          </span>
          <Select
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            value={String(serverMode ? pagination!.pageSize : table.getState().pagination.pageSize)}
            onValueChange={(v) => {
              const n = Number(v)
              if (Number.isNaN(n)) return
              if (serverMode && onPageSizeChange) {
                onPageSizeChange(n)
                onPageChange?.(1)
              } else {
                table.setPageSize(n)
              }
            }}
          >
            <SelectTrigger className='srd:h-8 srd:w-[72px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className='srd:text-neutrals-n-75 srd:flex srd:items-center srd:gap-1 srd:text-sm'>
            <Button
              variant='outline'
              size='icon'
              className='srd:h-8 srd:w-8'
              onClick={() => (serverMode ? onPageChange?.(currentPage - 1) : table.previousPage())}
              disabled={serverMode ? currentPage <= 1 : !table.getCanPreviousPage()}
            >
              <ChevronLeft className='srd:h-4 srd:w-4' />
            </Button>
            <span className='srd:min-w-16 srd:text-center'>
              {currentPage} / {pageCount || 1}
            </span>
            <Button
              variant='outline'
              size='icon'
              className='srd:h-8 srd:w-8'
              onClick={() => (serverMode ? onPageChange?.(currentPage + 1) : table.nextPage())}
              disabled={serverMode ? currentPage >= pageCount : !table.getCanNextPage()}
            >
              <ChevronRight className='srd:h-4 srd:w-4' />
            </Button>
          </div>
        </div>
      </div>

      <div className='srd:border-neutrals-n-20 srd:overflow-hidden srd:rounded-md srd:border'>
        <TableComponent>
          <TableHeader className='srd:bg-neutrals-nvar-10'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'srd:text-nowrap',
                      header.column.id === 'property' ? 'srd:min-w-0 srd:sm:w-[360px] srd:sm:max-w-[360px]' : '',
                    )}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={royaltyDetailsColumns.length}
                  className='srd:text-neutrals-n-75 srd:h-24 srd:text-center'
                >
                  No royalty details
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cell.column.id === 'property' ? 'srd:min-w-0 srd:whitespace-normal' : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </TableComponent>
      </div>
    </section>
  )
}
